import { auth } from "@/firebase/init";
import { db } from "@/firebase/init";
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  deleteUser, 
  updateEmail, 
  updatePassword, 
  EmailAuthProvider, 
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  sendPasswordResetEmail 
} from "firebase/auth";
import { doc, deleteDoc, setDoc, getDoc } from "firebase/firestore";
import { GoogleAuthProvider, signInWithPopup } from 'firebase/auth';

/**
 * Register a new user
 */
export const registerUser = async (email, password, role = "user") => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const userId = userCredential.user.uid;
    
    const profileData = {
      email,
      role, 
      name: "",
      profilePicture: "",
      registration: {
        date: new Date(),
        dateString: new Date().toString(),
        dateShort: new Date().toISOString().split("T")[0],
        month: new Date().toLocaleString("default", { month: "long" }),
        day: new Date().toLocaleDateString("en-US", { weekday: "long" }),
      },
      plan: {
        name: "free", //default plan  
        lastPaymentDate: null,
        nextBillingDate: null,
        renewsAt: null,
        endsAt: null,
        currency: 'USD',
        price: 0.00,
        active: true,
        generationsLimit: 10,
        subscriptionId: null,
        customerId: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        lemonSqueezyId: null,
        cardLastFour:null,
        cardBrand: null,        
      },
      stats: {
        lastGenerationDate: 0,
        periodStartDate: 0,
        videoIdeasGenerated: 0
      }
    };

    await setDoc(doc(db, "users", userId), profileData);
    console.log('Created new email user profile:', profileData);

    return { success: true, user: userCredential.user, profile: profileData };
  } catch (error) {
    console.error("Error registering user:", error);
    throw error;
  }
};

/**
 * Log in a user
 */
export const loginUser = async (email, password) => {
  try {
    return await signInWithEmailAndPassword(auth, email, password);
  } catch (error) {
    console.error("Login error:", error);
    throw error;
  }
};

/**
 * Log out a user
 */
export const logoutUser = async () => {
  try {
    return await signOut(auth);
  } catch (error) {
    console.error("Logout error:", error);
    throw error;
  }
};

/**
 * Re-authenticate user before sensitive actions
 */
export const reauthenticateUser = async (password) => {
  try {
    if (!auth.currentUser) throw new Error("No user is currently logged in.");
    
    const credential = EmailAuthProvider.credential(auth.currentUser.email, password);
    await reauthenticateWithCredential(auth.currentUser, credential);
  } catch (error) {
    console.error("Reauthentication error:", error);
    throw error;
  }
};

/**
 * Update user email (requires re-authentication)
 */
export const updateUserEmail = async (newEmail, password) => {
  try {
    await reauthenticateUser(password);
    await updateEmail(auth.currentUser, newEmail);
  } catch (error) {
    console.error("Error updating email:", error);
    throw error;
  }
};

/**
 * Update user password (requires re-authentication)
 */
export const updateUserPassword = async (newPassword, password) => {
  try {
    await reauthenticateUser(password);
    await updatePassword(auth.currentUser, newPassword);
  } catch (error) {
    console.error("Error updating password:", error);
    throw error;
  }
};

/**
 * Send password reset email
 */
export const sendPasswordReset = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    console.error("Error sending password reset email:", error);
    throw error;
  }
};

/**
 * Delete user account (works for both email/password and Google sign-in)
 */
export const deleteAccount = async (password = null) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("No user is currently logged in.");

    // Check if user signed in with Google
    const isGoogleUser = user.providerData.some(
      provider => provider.providerId === 'google.com'
    );

    if (isGoogleUser) {
      // For Google users, re-authenticate using popup
      const provider = new GoogleAuthProvider();
      try {
        await reauthenticateWithPopup(user, provider);
      } catch (error) {
        if (error.code === 'auth/popup-closed-by-user') {
          throw new Error("Please complete the Google sign-in to delete your account");
        }
        throw error;
      }
    } else {
      // For email/password users, re-authenticate with password
      if (!password) {
        throw new Error("Password is required to delete your account");
      }
      await reauthenticateUser(password);
    }
    
    // Delete user document from Firestore
    await deleteDoc(doc(db, "users", user.uid));
    
    // Delete authentication account
    await deleteUser(user);
  } catch (error) {
    console.error("Error deleting account:", error);
    throw error;
  }
};

/**
 * Sign in with Google
 */
export async function signInWithGoogle() {
  const provider = new GoogleAuthProvider();
  try {
    const result = await signInWithPopup(auth, provider);
    const user = result.user;

    // Check if user profile exists, if not create one
    const profileRef = doc(db, 'users', user.uid);
    const profileSnap = await getDoc(profileRef);

    let profileData;

    if (!profileSnap.exists()) {
      // Create profile for new Google user
      const [firstName] = (user.displayName || '').split(' ');
      profileData = {
        email: user.email,
        role: "user",
        name: firstName || '',
        profilePicture: user.photoURL || '',
        registration: {
          date: new Date(),
          dateString: new Date().toString(),
          dateShort: new Date().toISOString().split("T")[0],
          month: new Date().toLocaleString("default", { month: "long" }),
          day: new Date().toLocaleDateString("en-US", { weekday: "long" }),
        },
        plan: {
          name: "free",
          lastPaymentDate: null,
          nextBillingDate: null,
          renewsAt: null,
          endsAt: null,
          currency: 'USD',
          price: 0.00,
          active: true,
          generationsLimit: 10,
          subscriptionId: null,
          customerId: null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          lemonSqueezyId: null,
          cardLastFour: null,
          cardBrand: null,
        },
        stats: {
          lastGenerationDate: 0,
          periodStartDate: 0,
          videoIdeasGenerated: 0
        },
        meta: {
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          lastLogin: new Date().toISOString(),
          lastActivity: new Date().toISOString()
        }
      };

      await setDoc(profileRef, profileData);
      console.log('Created new Google user profile:', profileData);
    } else {
      // User exists, get existing profile data
      profileData = profileSnap.data();
      console.log('Existing Google user profile found:', profileData);
    }

    return { success: true, user, profile: profileData };
  } catch (error) {
    console.error('Google sign in error:', error);
    throw new Error(error.message || 'Failed to sign in with Google');
  }
}

/**
 * Helper function to handle post-authentication profile setup
 * This ensures the profile is available in the store immediately after sign-in
 */
export const handlePostAuthProfile = async (authResult) => {
  if (authResult.success && authResult.profile) {
    // Import store dynamically to avoid circular dependencies
    const store = await import('@/store').then(module => module.default);
    
    // Set profile in store immediately
    await store.dispatch('setProfile', authResult.profile);
    console.log('Profile set in store after authentication:', authResult.profile);
    
    return authResult;
  }
  
  // Fallback: fetch profile if not returned
  if (authResult.success && authResult.user) {
    const store = await import('@/store').then(module => module.default);
    await store.dispatch('fetchUserProfile');
    console.log('Profile fetched as fallback after authentication');
  }
  
  return authResult;
};
